import moment from 'moment'
import { useForm } from 'formular'
import { useConnect } from 'sb-redux-hook'
import type { ModalVisibilityProps } from 'sb-modal'
import { useCallback, useEffect, useMemo } from 'sb-hooks'
import actions from 'redux/actions'
import { array, snowplow } from 'common/helpers'
import getAllQueueProductsArray from 'common/helpers/getters/getAllQueueProductsArray'
import { integer, required } from 'helpers/validation'


export type UseSkipAMonthModalProps = SkipAMonthModal.ModalProps & ModalVisibilityProps


const useSkipAMonthModalBase = (props: UseSkipAMonthModalProps) => {
  const { isUnsubscribeFlow, openNextModal, closeModal, onClose } = props

  const { queue, firstName, nextBillingDate, isQueueFetching } = useConnect({
    queue: 'models.queue.items',
    firstName: 'models.me.firstName',
    nextBillingDate: 'models.subscription.nextBillingDate',
    isQueueFetching: 'models.queue.isFetching',
    isQueueFetched: 'models.queue.isQueueFetched',
  })

  const shouldFetchQueue = useMemo(() => !queue || queue.length < 3, [ queue ])

  // get first 3 products from the queue
  const products = useMemo(() => {
    if (!queue) {
      return []
    }

    const allProducts = getAllQueueProductsArray<SkipAMonthModal.Product>(queue, true)

    return allProducts.slice(0, 3)
  }, [ queue ])

  const skipOptions = useMemo(() => {
    const count = 3
    let restartBase = null

    if (nextBillingDate) {
      restartBase = moment(nextBillingDate)
    }
    else {
      // for dev cases without next billing date
      restartBase = moment()

      if (restartBase.date() >= 5) {
        // if it's 5th already, go to the next month
        restartBase.add(1, 'month')
      }

      restartBase.set('date', 5)
    }

    return array.range(1, count).map((monthCount) => ({
      monthCount,
      restartDate: restartBase.clone().add(monthCount, 'months'),
    }))
  }, [ nextBillingDate ])

  const form = useForm<SkipAMonthModal.FormValues>({
    fields: {
      monthCount: [ required, integer ],
      remind: [],
    },
    initialValues: {
      monthCount: 1,
      remind: true,
    },
  })

  const onContinue = useCallback(() => {
    closeModal()

    if (typeof openNextModal === 'function') {
      openNextModal()
    }
  }, [ closeModal, openNextModal ])

  useEffect(() => {
    if (shouldFetchQueue) {
      actions.models.queue.fetch({ limit: 3 })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldFetchQueue) {
      snowplow.track('UF skip popup', {
        withProducts: Boolean(products && products.length),
      })
    }
  }, [ products, shouldFetchQueue ])

  return {
    form,
    products,
    isProductsFetching: isQueueFetching,
    skipOptions,
    firstName,
    isUnsubscribeFlow,
    onContinue,
    onClose,
  }
}


export default useSkipAMonthModalBase
